import { Editor } from '@tiptap/react';
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Undo,
  Redo,
  AlignLeft,
  AlignCenter,
  AlignRight,
} from 'lucide-react';
import clsx from 'clsx';

interface ToolbarProps {
  editor: Editor;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
}

export default function Toolbar({ editor, canUndo, canRedo, onUndo, onRedo }: ToolbarProps) {
  const buttonClass = (isActive: boolean = false) =>
    clsx(
      'p-2 rounded hover:bg-gray-100 dark:hover:bg-surface-800 transition-colors',
      isActive
        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
        : 'text-gray-500'
    );

  return (
    <div className="flex items-center gap-1 mb-2 pb-2 border-b border-gray-200 dark:border-surface-800">
      {/* Text Formatting */}
      <button
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={buttonClass(editor.isActive('bold'))}
        title="Bold (Ctrl+B)"
      >
        <Bold size={18} />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={buttonClass(editor.isActive('italic'))}
        title="Italic (Ctrl+I)"
      >
        <Italic size={18} />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={buttonClass(editor.isActive('underline'))}
        title="Underline (Ctrl+U)"
      >
        <Underline size={18} />
      </button>

      <div className="w-px h-6 bg-gray-200 dark:bg-surface-800 mx-2" />

      {/* Lists */}
      <button
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={buttonClass(editor.isActive('bulletList'))}
        title="Bullet List"
      >
        <List size={18} />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={buttonClass(editor.isActive('orderedList'))}
        title="Numbered List"
      >
        <ListOrdered size={18} />
      </button>

      <div className="w-px h-6 bg-gray-200 dark:bg-surface-800 mx-2" />

      {/* Alignment */}
      <button
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        className={buttonClass(editor.isActive({ textAlign: 'left' }))}
        title="Align Left"
      >
        <AlignLeft size={18} />
      </button>
      <button
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        className={buttonClass(editor.isActive({ textAlign: 'center' }))}
        title="Align Center"
      >
        <AlignCenter size={18} />
      </button>
      <button
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        className={buttonClass(editor.isActive({ textAlign: 'right' }))}
        title="Align Right"
      >
        <AlignRight size={18} />
      </button>

      <div className="flex-1" />

      {/* Undo/Redo */}
      <button
        onClick={onUndo}
        disabled={!canUndo}
        className={clsx(buttonClass(), !canUndo && 'opacity-50 cursor-not-allowed')}
        title="Undo (Ctrl+Z)"
      >
        <Undo size={18} />
      </button>
      <button
        onClick={onRedo}
        disabled={!canRedo}
        className={clsx(buttonClass(), !canRedo && 'opacity-50 cursor-not-allowed')}
        title="Redo (Ctrl+Y)"
      >
        <Redo size={18} />
      </button>
    </div>
  );
}
